import Bb from 'backbone';
import validate from 'validate.js';

import BaseFormView from '../../../components/BaseForm';
import UserModel from '../../../../models/User';
import { redirectTo, Preloader } from '../../../../utils';
import Radio from "backbone.radio";

validate.Promise = Promise;

const errorMappingServerToClient = {
	'User is already linked to an NRL Account':
		'This old account is already linked to another NRL Account. ' +
		'Please click the back button below to link to a different user.'
};

const userChannel = Radio.channel('userModals');

class MatchStartPlayingView extends BaseFormView {

	get template() {
		// Overwrite in extended view
		return 'Overwrite template in extended view';
	}

	get className() {
		return "onboarding-form onboarding-match-start-playing";
	}

	get team_name_key() {
		// Overwrite in extended view
		return '';
	}

	get sponsor_key() {
		// Overwrite in extended view
		return '';
	}

	ui() {
		return {
			form     : '.js-match-start-playing-form',
			next     : '.js-next',
			back     : '.js-back',
			t_and_cs : '.js-t-and-cs',
			team_name: '.js-team-name',
			sponsor  : '.js-sponsor',
		};
	}

	events() {
		return {
			'click @ui.next'      : 'onClickNext',
			'click @ui.back'      : 'onClickBack',
			'change @ui.form'     : 'onChangeForm',
			'change @ui.team_name': 'onChangeTeamName'
		};
	}

	initialize() {
		this.onboarding = this.getOption('onboarding');
		this.model = new Bb.Model();
		this.team_name_changed = false;

		this.setListeners();
		this.setDefaultValues();
		this.addCheckTeamNameValidator();
	}

	addCheckTeamNameValidator() {
		const team_name_key = this.team_name_key;
		const team_name_error_mapping = {
			'Team name must be unique': 'Team name already exists'
		};

		validate.validators.checkTeamName = function(value) {
			return new validate.Promise(function(resolve, reject) {
				UserModel.checkTeamName({ [team_name_key]: value }).then(response => {
					if (!response.success) {
						const error = response.errors[0].text;
						const parsedError = team_name_error_mapping[error] || error;
						return resolve(parsedError);
					}
					return resolve();
				}).catch(reject);
			});
		};
	}

	setListeners() {
		this.listenTo(this.onboarding, 'user:link:success', this.onUserLinkSuccess);
		this.listenTo(this.onboarding, 'user:link:fail', this.onUserActionFail);
		this.listenTo(UserModel, 'user:logged:in', this.onUserLoginSuccess);
		this.listenTo(UserModel, 'user:login:fail', this.onUserActionFail);
		this.listenTo(UserModel, 'user:activate:success', this.onUserActivateSuccess);
		this.listenTo(UserModel, 'user:activate:fail', this.onUserActionFail);
		this.listenTo(UserModel, 'user:update:success', this.onUserUpdateSuccess);
		this.listenTo(UserModel, 'user:update:fail', this.onUserActionFail);
	}

	onChangeTeamName() {
		this.changeField('team_name');
		this.team_name_changed = Boolean(
			this.onboarding.get('user')[this.team_name_key] !== this.model.get('team_name')
		);
	}

	setDefaultValues() {
		let defaultValues = {
			firstname: this.onboarding.get('user').firstname || '',
			email    : this.onboarding.get('user').email || '',
			t_and_cs : 0,
			errors   : undefined,
			team_name: this.onboarding.get('user')[this.team_name_key] || '',
			sponsor  : 0
		};

		this.model.set(defaultValues);
	}

	getFormValues() {
		return {
			t_and_cs : this.getUI( 't_and_cs' ).filter( ':checked' ).val() ? 1: 0,
			team_name: this.getUI( 'team_name' ).val(),
			sponsor  : this.getUI( 'sponsor' ).filter( ':checked' ).val() ? 1 : 0,
		};
	}

	validationRules() {
		return {
			t_and_cs: {
				exclusion: {
					within: [0],
					message: "Terms and Conditions must be checked"
				}
			},
			team_name:  {
				presence: {
					allowEmpty: false,
					message: "Team name is required"
				}
			}
		};
	}

	hasCustomChangeMethod(name) {
		const custom_change_method_fields = ['team_name'];

		return custom_change_method_fields.indexOf(name) !== -1;
	}

	onClickBack(e) {
		e.preventDefault();
		this.onboarding.set({
			current_step: this.onboarding.get('MatchStartPlaying_back_url')
		});
	}

	async submit() {
		const errors = this.validate();

		if (errors) {
			this.attachValidationErrors(errors);
			return false;
		}

		Preloader.show();

		await this.validateOnServerPreSubmit().then(
			this.completeUserAccount.bind(this),
			this.attachValidationErrors.bind(this)
		);
	}

	async completeUserAccount() {
		const identity_token = UserModel.get('identity_token');
		await this.onboarding.linkUserAccounts(identity_token);
	}

	async onUserLinkSuccess() {
		const identity_token = UserModel.get('identity_token');
		await UserModel.login({ identity_token });
	}

	async onUserLoginSuccess() {
		let activateRequestBody = {
			[this.sponsor_key]: this.model.get('sponsor')
		};

		await UserModel.activateUser(activateRequestBody);
	}

	async onUserActivateSuccess() {
		if (this.team_name_changed) {
			await this.onTeamNameChanged();
		}
		else {
			Preloader.hide();
			this.onboarding.clearModelAndStore();

			this.completionRedirect();
		}
	}

	async onTeamNameChanged() {
		UserModel.set({
			[this.team_name_key]: this.model.get('team_name')
		});
		await UserModel.updateUserData({
			[this.team_name_key]: this.model.get('team_name')
		});
	}

	onUserUpdateSuccess() {
		Preloader.hide();
		this.onboarding.clearModelAndStore();

		this.completionRedirect();
	}

	completionRedirect() {
		// Overwrite this in extended view
		redirectTo();
		userChannel.trigger('hide:onboarding:modal');
	}

	onUserActionFail(errors) {
		Preloader.hide();
		const parsed_errors = errors.map(
			error => errorMappingServerToClient[error.text] || error.text
		);
		this.attachValidationErrors({
			server_side: parsed_errors
		});
	}

	serverValidationRulesPreSubmit() {
		const serverValidationRules = {
			team_name: {
				checkTeamName: () => {
					return this.team_name_changed;
				}
			}
		};

		return serverValidationRules;
	}
}

export default MatchStartPlayingView;